import * as React from 'react';

import { ButtonBase, CircularProgress, Typography } from '@mui/material';

import {
  isArtifactNotFound,
  signArtifactUrl,
} from '@modules/_shared/components/chat/artifactApi';
import {
  type ArtifactSummary,
  createRequestGate,
} from '@modules/_shared/components/chat/artifactFiles';
import { useLocale } from '@shared/hooks/locale';
import { useTheme } from '@shared/theme/theme';

export const THUMBNAIL_MAX_HEIGHT = 160;

type ImageState =
  | { kind: 'loading' }
  | { kind: 'ready'; src: string; retried: boolean }
  | { kind: 'gone' }
  | { kind: 'failed' };

// A signed inline URL to show one of the user's images in a card (the chat
// API serves images inline). An image that fails to load is signed again once, as
// in the Files view: the 15 min URL may have expired (a tab left open). A 404
// means the file was deleted (or is not this user's).
const useArtifactImage = (id: string) => {
  const [state, setState] = React.useState<ImageState>({ kind: 'loading' });
  const [gate] = React.useState(createRequestGate);

  const sign = React.useCallback(
    async (retried: boolean) => {
      const isCurrent = gate.start();
      try {
        const src = await signArtifactUrl(id, { download: false });
        if (isCurrent()) {
          setState({ kind: 'ready', src, retried });
        }
      } catch (error) {
        if (!isCurrent()) {
          return;
        }
        setState({ kind: isArtifactNotFound(error) ? 'gone' : 'failed' });
      }
    },
    [gate, id],
  );

  React.useEffect(() => {
    void sign(false);
    return gate.cancel;
  }, [gate, sign]);

  const onImageError = () => {
    if (state.kind === 'ready' && !state.retried) {
      setState({ kind: 'loading' });
      void sign(true);
    } else {
      setState({ kind: 'failed' });
    }
  };

  return { state, onImageError };
};

// A thumbnail of an image in the user's Files; clicking it opens the preview
// (onOpen: the shared lightbox, useArtifactPreview). Cropped (object-fit:
// cover) to keep chat cards compact.
export const ArtifactThumbnail = ({
  artifact,
  onOpen,
  height = THUMBNAIL_MAX_HEIGHT,
}: {
  artifact: ArtifactSummary;
  onOpen: () => void;
  height?: number;
}) => {
  const { t } = useLocale();
  const { colors } = useTheme();
  const { state, onImageError } = useArtifactImage(artifact.id);

  const box = {
    width: '100%',
    height,
    borderRadius: 6,
    backgroundColor: colors.grey100,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  } as const;

  if (state.kind !== 'ready') {
    return (
      <div css={box} title={artifact.name}>
        {state.kind === 'loading' ? (
          <CircularProgress size={18} />
        ) : (
          <Typography
            variant="caption"
            sx={{ color: colors.grey600, px: 1, textAlign: 'center' }}
          >
            {state.kind === 'gone'
              ? t('This file was deleted, or it was saved in another agency.')
              : t('Failed to load the preview')}
          </Typography>
        )}
      </div>
    );
  }

  return (
    <ButtonBase
      onClick={onOpen}
      title={artifact.name}
      aria-label={artifact.name}
      sx={box}
    >
      <img
        src={state.src}
        alt={artifact.name}
        loading="lazy"
        onError={onImageError}
        css={{ width: '100%', height: '100%', objectFit: 'cover' }}
      />
    </ButtonBase>
  );
};
